import React, { useEffect } from 'react';
import { createStackNavigator, CardStyleInterpolators } from '@react-navigation/stack';
import { useAppSelector, useAppDispatch } from '@/store';
import { checkAuthStatus, restoreActiveContext } from '@/store/slices/authSlice';
import { AuthNavigator } from './residents/mobile/AuthNavigator';
import { ResidentMobileLayout } from '@/components/residents/mobile/ResidentMobileLayout';
import { AdminNavigator } from './admin/AdminNavigator';
import { BulletinsListScreen } from '@/screens/residents/bulletins/BulletinsListScreen';
import { BulletinDetailScreen } from '@/screens/residents/bulletins/BulletinDetailScreen';
import { EventDetailScreen } from '@/screens/residents/events/EventDetailScreen';
import { ResourceDetailScreen } from '@/screens/residents/resources/ResourceDetailScreen';
import { MoreResourcesScreen } from '@/screens/residents/resources/MoreResourcesScreen';
import { MobileResourceDetailScreen } from '@/screens/residents/resources/mobile/MobileResourceDetailScreen';
import { PdfScreen } from '@/screens/residents/resources/PdfScreen';
import { ContactOfficeScreen } from '@/screens/residents/contact/ContactOfficeScreen';
import { MobileContactOfficeScreen } from '@/screens/residents/contact/mobile/MobileContactOfficeScreen';
import { CommunityMarketplaceScreen } from '@/screens/residents/marketplace/CommunityMarketplaceScreen';
import { MarketplaceItemDetailScreen } from '@/screens/residents/marketplace/MarketplaceItemDetailScreen';
import { SellerProfileScreen } from '@/screens/residents/marketplace/SellerProfileScreen';
import { MarketplaceProfileScreen } from '@/screens/residents/marketplace/MarketplaceProfileScreen';
import { MyListingItemDetailScreen } from '@/screens/residents/marketplace/MyListingItemDetailScreen';
import { SavedListingsScreen } from '@/screens/residents/marketplace/SavedListingsScreen';
import { SavedItemDetailScreen } from '@/screens/residents/marketplace/SavedItemDetailScreen';
import { MarketplaceInboxScreen } from '@/screens/residents/marketplace/MarketplaceInboxScreen';
import { InboxConversationScreen } from '@/screens/residents/marketplace/InboxConversationScreen';
import { MarketplaceFollowingScreen } from '@/screens/residents/marketplace/MarketplaceFollowingScreen';
import { WebViewScreen } from '@/screens/residents/web/WebViewScreen';
import { LinkedAccountsScreen } from '@/screens/residents/linkedAccounts/LinkedAccountsScreen';
import { NewInvitationScreen } from '@/screens/residents/invitations/NewInvitationScreen';
import { ActivityIndicator, View, StyleSheet, Platform } from 'react-native';
import SystemNavigationBar from 'react-native-system-navigation-bar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { DesktopNavigator } from './residents/desktop/DesktopNavigator';
import { ContextPickerScreen } from '@/screens/residents/auth/ContextPickerScreen';
import { NoAccessScreen } from '@/screens/residents/auth/NoAccessScreen';
import { resolveAuthRoute } from './authRouting';
import { notificationStrings, newInvitationStrings, profileStrings } from '@/constants/residents/strings';
import { WifiConnectionScreen } from '@/screens/residents/wifi/WifiConnectionScreen';
import { ProfileBioScreen } from '@/screens/residents/profile/ProfileBioScreen';
import { RoommatesScreen } from '@/screens/residents/profile/RoommatesScreen';
import { HowItWorksScreen } from '@/screens/residents/profile/HowItWorksScreen';
import { TutorialScreen } from '@/screens/residents/profile/TutorialScreen';
import { DeleteAccountScreen, DeleteAccountConfirmedScreen } from '@/screens/residents/profile/DeleteAccountScreen';
import { AccountVerificationScreen } from '@/screens/residents/auth/AccountVerificationScreen';
import { SettingsModalScreen } from '@/screens/residents/settings/SettingsModalScreen';
import { Bulletin } from '@/types/residents/bulletin';
import { CommunityResource } from '@/types/residents/communityResource';
import { Event as ResioEvent } from '@/types/residents/event';
import { MarketplaceListing, MyListing, SavedListing, PostItemFormData } from '@/types/residents/marketplaceListing';
import { buildCenteredHeader } from '@/constants/residents/mobile/header';
import { CommunityBoardScreen } from '@/screens/residents/corkBoard/CommunityBoardScreen';
import { ThreadDetailScreen } from '@/screens/residents/corkBoard/ThreadDetailScreen';
import { CreateThreadScreen } from '@/screens/residents/corkBoard/CreateThreadScreen';
import { EditThreadScreen } from '@/screens/residents/corkBoard/EditThreadScreen';
import { MyThreadsScreen } from '@/screens/residents/corkBoard/MyThreadsScreen';
import { SavedThreadsScreen } from '@/screens/residents/corkBoard/SavedThreadsScreen';
import { SurveyResponsesScreen } from '@/screens/admin/corkBoard/SurveyResponsesScreen';
// Mobile payment screens
import { MobilePaymentDetailScreen } from '@/screens/residents/payments/mobile/MobilePaymentDetailScreen';
import { MobileMakePaymentScreen } from '@/screens/residents/payments/mobile/MobileMakePaymentScreen';
import { MobileAutopaySetupScreen } from '@/screens/residents/payments/mobile/MobileAutopaySetupScreen';
import { MobileManageWalletScreen } from '@/screens/residents/payments/mobile/MobileManageWalletScreen';
import { MobileAutopayManagementScreen } from '@/screens/residents/account/mobile/MobileAutopayManagementScreen';
import { MobileAccountScreen } from '@/screens/residents/account/mobile/MobileAccountScreen';
import { PaymentSummaryModal } from '@/screens/residents/payments/PaymentSummaryModal';
import { TransactionHistoryScreen } from '@/screens/residents/payments/TransactionHistoryScreen';
// Maintenance screens (new Figma design)
import { MobileMaintenanceDetailScreen } from '@/screens/residents/maintenance/mobile/MobileMaintenanceDetailScreen';
import { MobileCreateMaintenanceScreen } from '@/screens/residents/maintenance/mobile/MobileCreateMaintenanceModal';
import { MobilePostItemScreen } from '@/screens/residents/marketplace/MobilePostItemScreen';
import { ConversationScreen } from '@/screens/residents/messages/ConversationScreen';
import { NewConversationScreen } from '@/screens/residents/messages/NewConversationScreen';

export type RootStackParamList = {
  Auth: undefined;
  Main: undefined;
  Admin: undefined;
  // Unified Auth (RES-89): logged-in context picker + zero-context state.
  ContextPicker: undefined;
  NoAccess: undefined;
  // Bulletins screens at root level (iOS-style full screen)
  BulletinsList: undefined;
  BulletinDetail: {
    bulletin: Bulletin;
  };
  // Events screens
  EventDetail: {
    event: ResioEvent;
  };
  // WiFi screen
  WifiConnection: undefined;
  // Community Resources screens
  ResourceDetail: {
    resource: CommunityResource;
  };
  MobileResourceDetail: {
    resource: CommunityResource;
  };
  MoreResources: undefined;
  PdfScreen: {
    url: string;
    title: string;
  };
  // Community marketplace
  CommunityMarketplace: undefined;
  PostItem: {
    propertyId?: number;
    initialData?: PostItemFormData;
    isEditMode?: boolean;
    listingId?: string;
  } | undefined;
  MarketplaceItemDetail: {
    listing: MarketplaceListing;
  };
  SellerProfile: {
    sellerId: string;
  };
  MarketplaceProfile: {
    action?: 'sold' | 'removed' | 'reposted';
    listingId?: string;
  } | undefined;
  MyListingItemDetail: {
    listing: MyListing;
  };
  // Saved listings
  SavedListings: {
    unsavedId?: string;
  } | undefined;
  SavedItemDetail: {
    listing: SavedListing;
  };
  // Marketplace Inbox
  MarketplaceInbox: undefined;
  InboxConversation: {
    conversationId: string;
  };
  // Marketplace Following
  MarketplaceFollowing: undefined;
  // Contact Office & FAQ screens
  ContactOffice: undefined;
  MobileContactOffice: undefined;
  WebView: {
    url: string;
    title: string;
  };
  // Linked Accounts screen
  LinkedAccounts: undefined;
  // New Invitation screen
  NewInvitation: undefined;
  // Account Verification (accessible from Profile -> Password)
  AccountVerification: { email: string; isSignup?: boolean } | undefined;
  // Settings (modal)
  SettingsModal: undefined;
  // Profile-related
  ProfileBio: undefined;
  Roommates: undefined;
  RoommateDetail: { roommate: any };
  HowItWorks: undefined;
  Tutorial: undefined;
  DeleteAccount: undefined;
  DeleteAccountConfirmed: undefined;
  CreateMaintenanceTicket: undefined;
  MaintenanceDetail: { ticket: any };
  // Payment screens (mobile)
  PaymentDetail: { leaseId: number };
  TransactionHistory: { leaseId: number };
  MakePayment: { leaseId: number };
  AutopaySetup: { leaseId: number; edit?: boolean };
  ManageWallet: { initialScreen?: 'add' };
  MobileAutopayManagement: undefined;
  // Account settings (mobile)
  MobileAccountSettings: { section?: string } | undefined;
  // Payment modal (desktop)
  PaymentSummaryModal: { leaseId: number; residentId?: number };
  // Property Chat (fullscreen modals)
  Conversation: { conversationId: number; subject?: string };
  NewConversation: { initialSubject?: string } | undefined;
  // Community Cork Board
  CommunityBoard: undefined;
  ThreadDetail: { threadId: string };
  CreateThread: undefined;
  EditThread: { threadId: string };
  MyThreads: undefined;
  SavedThreads: undefined;
  SurveyResponses: { threadId: string };
};

const Stack = createStackNavigator<RootStackParamList>();

export const RootNavigator: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, availableContexts, activeContext } = useAppSelector((state) => state.auth);
  const [isChecking, setIsChecking] = React.useState(true);
  const { layoutMode, isWeb } = useResponsive();
  const { colors, semantic } = useThemeColors();
  // Memoize static header options to avoid re-setting options on every re-render (prevents web thrash)
  const bulletinsHeaderOptions = React.useMemo(() => buildCenteredHeader(notificationStrings.title), []);

  // Unified Auth (RES-89): route on the chosen context's kind, not the old
  // binary `user.roles.length > 0` discriminator. 'picker' when the account has
  // more than one context and none chosen; 'no-access' when it has none.
  const authRoute = resolveAuthRoute({ activeContext, availableContexts });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'android') {
      SystemNavigationBar.setNavigationColor(
        isAuthenticated ? semantic.background.primary : PRIMITIVE_COLORS.white
      );
    }
  }, [isAuthenticated, semantic.background.primary]);

  const checkAuth = async () => {
    // Always validate session on startup to avoid stale persisted auth
    try {
      await dispatch(checkAuthStatus()).unwrap();
      // Unified Auth (RES-89): the persisted `availableContexts` is now
      // rehydrated; re-select the single context (through the setActiveContext
      // action, so adminUI initializes). Multi-context sessions re-show the picker.
      dispatch(restoreActiveContext());
    } catch (error) {
      console.log('❌ RootNavigator: Auth check failed:', error);
      // User not authenticated
    } finally {
      setIsChecking(false);
    }
  };

  if (isChecking) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: colors.bg.base }]}>
        <ActivityIndicator size="large" color={semantic.interactive.primary} />
      </View>
    );
  }

  return (
    <Stack.Navigator screenOptions={{ headerShown: false, cardStyle: { backgroundColor: colors.bg.base } }}>
      {!isAuthenticated ? (
        <Stack.Screen name="Auth" component={AuthNavigator} />
      ) : authRoute === 'picker' ? (
        <Stack.Screen name="ContextPicker" component={ContextPickerScreen} />
      ) : authRoute === 'no-access' ? (
        <Stack.Screen name="NoAccess" component={NoAccessScreen} />
      ) : (
        <>
          {/* Unified Auth (RES-89): route on the chosen context's kind. */}
          {authRoute === 'admin' ? (
            <Stack.Screen name="Admin" component={AdminNavigator} />
          ) : isWeb && layoutMode === 'desktop' ? (
            <Stack.Screen name="Main" component={DesktopNavigator} />
          ) : (
            // Using new Figma-based navigation with header menu (instead of bottom tab navigation)
            <Stack.Screen name="Main" component={ResidentMobileLayout} />
          )}
          {/* Bulletins screens presented full-screen (iOS style) */}
          <Stack.Screen name="BulletinsList" component={BulletinsListScreen} options={bulletinsHeaderOptions} />
          <Stack.Screen 
            name="BulletinDetail" 
            component={BulletinDetailScreen}
            options={{
              headerShown: false,
              presentation: 'modal', // iOS-style modal presentation
              cardStyle: { backgroundColor: 'transparent' }, // Hide modal background
            }}
          />
          <Stack.Screen 
            name="EventDetail" 
            component={EventDetailScreen}
            options={{
              headerShown: false,
              presentation: 'modal', // iOS-style modal presentation
              cardStyle: { backgroundColor: 'transparent' }, // Hide modal background
            }}
          />
          <Stack.Screen
            name="WifiConnection"
            component={WifiConnectionScreen}
            options={{
              headerShown: false,
              presentation: 'card',
            }}
          />
        <Stack.Screen name="ProfileBio" component={ProfileBioScreen} options={{ headerShown: false, presentation: 'card' }} />
        <Stack.Screen name="Roommates" component={RoommatesScreen} options={buildCenteredHeader(profileStrings.option.roommates)} />
        <Stack.Screen name="HowItWorks" component={HowItWorksScreen} options={{ headerShown: false, presentation: 'card' }} />
        <Stack.Screen 
          name="Tutorial" 
          component={TutorialScreen}
          options={{ headerShown: false, presentation: 'modal' }}
        />
        <Stack.Screen name="RoommateDetail" component={require('@/screens/residents/profile/RoommateDetailScreen').RoommateDetailScreen} options={buildCenteredHeader('Roommate')} />
        <Stack.Screen name="DeleteAccount" component={DeleteAccountScreen} options={{ headerShown: false, presentation: 'card' }} />
        <Stack.Screen 
          name="DeleteAccountConfirmed" 
          component={DeleteAccountConfirmedScreen}
          options={{ headerShown: false, presentation: 'modal' }}
          />
        {/* Maintenance screens (new Figma design) */}
        <Stack.Screen 
          name="CreateMaintenanceTicket" 
          component={MobileCreateMaintenanceScreen}
          options={{
            headerShown: false,
            presentation: 'modal',
            cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
          }}
        />
        <Stack.Screen
          name="MaintenanceDetail"
          component={MobileMaintenanceDetailScreen}
          options={{
            headerShown: false,
            presentation: 'modal',
            // Match the app-wide modal transition (create, post-item, chat).
            cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
          }}
        />
          <Stack.Screen name="ResourceDetail" component={ResourceDetailScreen} options={{ ...buildCenteredHeader(''), presentation: 'card' }} />
          <Stack.Screen name="MobileResourceDetail" component={MobileResourceDetailScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="MoreResources" component={MoreResourcesScreen} options={{ ...buildCenteredHeader('More resources'), presentation: 'card' }} />
          <Stack.Screen name="PdfScreen" component={PdfScreen} options={{ ...buildCenteredHeader(''), presentation: 'card' }} />
          <Stack.Screen name="CommunityMarketplace" component={CommunityMarketplaceScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen 
            name="PostItem" 
            component={MobilePostItemScreen}
            options={{
              headerShown: false,
              presentation: 'modal',
              cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
            }}
          />
          <Stack.Screen name="MarketplaceItemDetail" component={MarketplaceItemDetailScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="SellerProfile" component={SellerProfileScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="MarketplaceProfile" component={MarketplaceProfileScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="MyListingItemDetail" component={MyListingItemDetailScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="SavedListings" component={SavedListingsScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="SavedItemDetail" component={SavedItemDetailScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="MarketplaceInbox" component={MarketplaceInboxScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="InboxConversation" component={InboxConversationScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="MarketplaceFollowing" component={MarketplaceFollowingScreen} options={{ headerShown: false, presentation: 'card' }} />
          {/* Newsfeed */}
          <Stack.Screen name="CommunityBoard" component={CommunityBoardScreen} options={{ ...buildCenteredHeader('Newsfeed'), presentation: 'card' }} />
          <Stack.Screen name="ThreadDetail" component={ThreadDetailScreen} options={{ headerShown: false, presentation: 'card' }} />
          {/* Property Chat modals */}
          <Stack.Screen name="Conversation" component={ConversationScreen} options={{ headerShown: false, presentation: 'modal', cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS }} />
          <Stack.Screen name="NewConversation" component={NewConversationScreen} options={{ headerShown: false, presentation: 'modal', cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS }} />
          <Stack.Screen name="CreateThread" component={CreateThreadScreen} options={{ headerShown: false, presentation: 'modal' }} />
          <Stack.Screen name="EditThread" component={EditThreadScreen} options={{ headerShown: false, presentation: 'modal' }} />
          <Stack.Screen name="MyThreads" component={MyThreadsScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="SavedThreads" component={SavedThreadsScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="SurveyResponses" component={SurveyResponsesScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="ContactOffice" component={ContactOfficeScreen} options={{ ...buildCenteredHeader('Contact the office'), presentation: 'card' }} />
          <Stack.Screen name="MobileContactOffice" component={MobileContactOfficeScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="WebView" component={WebViewScreen} options={{ headerShown: false, presentation: 'card' }} />
          <Stack.Screen name="LinkedAccounts" component={LinkedAccountsScreen} options={{ headerShown: false, presentation: 'card' }} />
          
          <Stack.Screen name="NewInvitation" component={NewInvitationScreen} options={buildCenteredHeader(newInvitationStrings.title)} />
          {/* Account Verification screen accessible from Profile -> Password */}
          <Stack.Screen 
            name="AccountVerification" 
            component={AccountVerificationScreen}
            options={{
              headerShown: false,
              presentation: 'card',
            }}
          />
          {/* Mobile Payment Screens */}
          <Stack.Screen 
            name="PaymentDetail" 
            component={MobilePaymentDetailScreen}
            options={{ headerShown: false, presentation: 'card' }}
          />
          <Stack.Screen 
            name="TransactionHistory" 
            component={TransactionHistoryScreen}
            options={{ headerShown: false, presentation: 'card' }}
          />
          <Stack.Screen 
            name="MakePayment" 
            component={MobileMakePaymentScreen}
            options={{ headerShown: false, presentation: 'modal' }}
          />
          <Stack.Screen 
            name="ManageWallet" 
            component={MobileManageWalletScreen}
            options={{ headerShown: false, presentation: 'modal' }}
          />
          <Stack.Screen 
            name="AutopaySetup" 
            component={MobileAutopaySetupScreen}
            options={{ headerShown: false, presentation: 'card' }}
          />
          <Stack.Screen 
            name="MobileAutopayManagement" 
            component={MobileAutopayManagementScreen}
            options={{ headerShown: false, presentation: 'card' }}
          />
          {/* Account Settings (mobile) */}
          <Stack.Screen 
            name="MobileAccountSettings" 
            component={MobileAccountScreen}
            options={{ headerShown: false, presentation: 'card' }}
          />
          {/* Desktop Payment Modal */}
          <Stack.Screen 
            name="PaymentSummaryModal" 
            component={PaymentSummaryModal}
            options={{ headerShown: false, presentation: 'transparentModal' }}
          />
        </>
      )}
      {/* Settings modal - available regardless of auth state once loaded */}
      <Stack.Screen name="SettingsModal" component={SettingsModalScreen} options={{ ...buildCenteredHeader(notificationStrings.section.header), presentation: 'modal' }} />
    </Stack.Navigator>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
}); 